import { useAuth0 } from "@auth0/auth0-react";
import { useCallback } from "react";
import { AUTH_DISABLED } from "./devMode";
import { currentReturnTo } from "./returnTo";

// Refresh when the ID token is within this margin of expiring, so a token
// that's about to lapse doesn't get sent mid-flight.
const EXPIRY_MARGIN_MS = 30_000;

/** Silent-auth failures that mean "session's gone — sign in again". */
function isLoginRequired(err: unknown): boolean {
  const code = (err as { error?: string } | null)?.error;
  return (
    code === "login_required" ||
    code === "consent_required" ||
    code === "interaction_required" ||
    code === "missing_refresh_token" ||
    code === "invalid_grant"
  );
}

/**
 * Returns a getter for the bearer token sent to gft-insights-api.
 *
 * Matching the org pattern (gft-product-service, gft-cohort-service), the API
 * validates the Auth0 ID token — audience = the SPA client ID — which natively
 * carries email/email_verified, so no Auth0 Action is needed.
 *
 * Pitfall this getter exists to handle: getAccessTokenSilently() serves from
 * cache while the *access* token is alive, so the *ID* token (what the API
 * actually checks) can expire without ever triggering a refresh. The getter
 * inspects the ID token's own exp and forces a cacheMode:"off" refresh when
 * it's stale — callers can also force one after a 401 via
 * `getToken({ forceRefresh: true })`. When silent auth is no longer possible
 * (refresh token expired/revoked), it redirects to login instead of
 * surfacing an error.
 */
export function useAuthToken(): (options?: {
  forceRefresh?: boolean;
}) => Promise<string> {
  const { getAccessTokenSilently, getIdTokenClaims, loginWithRedirect } =
    useAuth0();

  return useCallback(
    async ({ forceRefresh = false } = {}) => {
      if (AUTH_DISABLED) return "local-dev";
      try {
        await getAccessTokenSilently(
          forceRefresh ? { cacheMode: "off" } : undefined,
        );
        let claims = await getIdTokenClaims();
        const expired =
          claims?.exp !== undefined &&
          claims.exp * 1000 < Date.now() + EXPIRY_MARGIN_MS;
        if (!forceRefresh && expired) {
          await getAccessTokenSilently({ cacheMode: "off" });
          claims = await getIdTokenClaims();
        }
        if (!claims?.__raw) {
          throw new Error("Not authenticated");
        }
        return claims.__raw;
      } catch (err) {
        if (isLoginRequired(err)) {
          await loginWithRedirect({
            appState: { returnTo: currentReturnTo() },
          });
          // The browser is navigating away; stop the caller's flow quietly.
          throw new Error("Session expired — redirecting to sign-in");
        }
        throw err;
      }
    },
    [getAccessTokenSilently, getIdTokenClaims, loginWithRedirect],
  );
}
